import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Swal from 'sweetalert2';
import { FiMapPin, FiSearch, FiSend, FiTruck } from 'react-icons/fi';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Select } from '../../components/Select';
import { Table } from '../../components/Table';
import {
  atribuirMotoboyPedido,
  criarPedidoManual,
  listarClientesHistorico,
  listarPedidos,
  PedidoManualPayload,
} from '../../api/pedidos';
import { listarFuncionarios } from '../../api/funcionarios';
import { ClienteHistorico, Funcionario, Pedido, PedidoOrigem, PedidoStatus } from '../../types';
import { formatarCep } from '../../utils/mascaras';
import { formatarData, formatarMoeda } from '../../utils/formatadores';

type FormPedido = {
  numero_pedido: string;
  nome_cliente: string;
  origem: PedidoOrigem;
  cep: string;
  logradouro: string;
  numero_endereco: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  observacoes: string;
  motoboy_id: string;
};

const FORM_INICIAL: FormPedido = {
  numero_pedido: '',
  nome_cliente: '',
  origem: 'manual',
  cep: '',
  logradouro: '',
  numero_endereco: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  observacoes: '',
  motoboy_id: '',
};

const ORIGENS: Array<{ value: PedidoOrigem; label: string }> = [
  { value: 'manual', label: 'Manual' },
  { value: 'ifood', label: 'iFood' },
  { value: '99food', label: '99Food' },
  { value: 'ubereats', label: 'Uber Eats' },
  { value: 'cardapio_proprio', label: 'Cardapio proprio' },
];

const STATUS_LABEL: Record<PedidoStatus, string> = {
  recebido: 'Recebido',
  preparo: 'Em preparo',
  pronto: 'Pronto',
  em_entrega: 'Em entrega',
  entregue: 'Entregue',
  cancelado: 'Cancelado',
};

const STATUS_TONE: Record<PedidoStatus, 'neutro' | 'primario' | 'sucesso' | 'alerta'> = {
  recebido: 'neutro',
  preparo: 'primario',
  pronto: 'alerta',
  em_entrega: 'primario',
  entregue: 'sucesso',
  cancelado: 'neutro',
};

const ORIGEM_LABEL: Record<PedidoOrigem, string> = {
  manual: 'Manual',
  ifood: 'iFood',
  '99food': '99Food',
  ubereats: 'Uber Eats',
  cardapio_proprio: 'Cardapio proprio',
};

function enderecoPedido(pedido: Pick<Pedido, 'logradouro' | 'numero_endereco' | 'bairro' | 'cidade' | 'uf' | 'complemento'>) {
  const linha = [pedido.logradouro, pedido.numero_endereco].filter(Boolean).join(', ');
  const bairroCidade = [pedido.bairro, pedido.cidade, pedido.uf].filter(Boolean).join(' - ');
  const complemento = pedido.complemento ? `, ${pedido.complemento}` : '';
  return [linha ? `${linha}${complemento}` : null, bairroCidade].filter(Boolean).join(' | ');
}

async function consultarCep(cep: string) {
  const resposta = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
  const dados = await resposta.json();

  if (!resposta.ok || dados.erro) {
    throw new Error('CEP nao encontrado.');
  }

  return {
    logradouro: dados.logradouro || '',
    bairro: dados.bairro || '',
    cidade: dados.localidade || '',
    uf: dados.uf || '',
  };
}

export function PedidosLista() {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [clientes, setClientes] = useState<ClienteHistorico[]>([]);
  const [motoboys, setMotoboys] = useState<Funcionario[]>([]);
  const [form, setForm] = useState<FormPedido>(FORM_INICIAL);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [atribuindoId, setAtribuindoId] = useState<number | null>(null);
  const [consultandoCep, setConsultandoCep] = useState(false);
  const [erroCep, setErroCep] = useState<string | null>(null);
  const [buscaCliente, setBuscaCliente] = useState('');
  const ultimoCepRef = useRef('');

  async function carregarTudo() {
    setCarregando(true);
    try {
      const [pedidosData, funcionariosData, clientesData] = await Promise.all([
        listarPedidos(),
        listarFuncionarios(),
        listarClientesHistorico(),
      ]);

      setPedidos(pedidosData);
      setMotoboys(funcionariosData.filter((item) => item.eh_motoboy));
      setClientes(clientesData);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    void carregarTudo();
  }, []);

  useEffect(() => {
    const cep = form.cep.replace(/\D/g, '');

    if (cep.length !== 8 || cep === ultimoCepRef.current) {
      return;
    }

    ultimoCepRef.current = cep;
    setConsultandoCep(true);
    setErroCep(null);

    consultarCep(cep)
      .then((dados) => {
        setForm((atual) => ({
          ...atual,
          logradouro: dados.logradouro,
          bairro: dados.bairro,
          cidade: dados.cidade,
          uf: dados.uf,
        }));
      })
      .catch((error: Error) => {
        setErroCep(error.message);
      })
      .finally(() => setConsultandoCep(false));
  }, [form.cep]);

  const clientesFiltrados = useMemo(() => {
    const termo = buscaCliente.trim().toLowerCase();
    if (!termo) return clientes;
    return clientes.filter((cliente) => cliente.nome.toLowerCase().includes(termo));
  }, [clientes, buscaCliente]);

  const motoboysComApp = useMemo(
    () => motoboys.filter((item) => item.eh_motoboy && item.app_motoboy_ativo),
    [motoboys]
  );

  const motoboysSemApp = useMemo(
    () => motoboys.filter((item) => item.eh_motoboy && !item.app_motoboy_ativo),
    [motoboys]
  );

  const pedidosAtivos = useMemo(
    () => pedidos.filter((pedido) => pedido.status !== 'entregue' && pedido.status !== 'cancelado'),
    [pedidos]
  );

  const pedidosEmEntrega = pedidos.filter((pedido) => pedido.status === 'em_entrega').length;

  function atualizarCampo<K extends keyof FormPedido>(campo: K, valor: FormPedido[K]) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);

    const payload: PedidoManualPayload = {
      numero_pedido: form.numero_pedido,
      nome_cliente: form.nome_cliente,
      origem: form.origem,
      cep: form.cep,
      logradouro: form.logradouro,
      numero_endereco: form.numero_endereco,
      complemento: form.complemento || undefined,
      bairro: form.bairro,
      cidade: form.cidade,
      uf: form.uf,
      observacoes: form.observacoes || undefined,
      motoboy_id: form.motoboy_id ? Number(form.motoboy_id) : null,
    };

    try {
      await criarPedidoManual(payload);
      setForm(FORM_INICIAL);
      ultimoCepRef.current = '';
      setErroCep(null);
      await carregarTudo();
      Swal.fire({ icon: 'success', title: 'Pedido encaminhado!', timer: 1200, showConfirmButton: false });
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Nao foi possivel criar o pedido',
        text: error.response?.data?.erro || 'Revise os dados informados.',
      });
    } finally {
      setSalvando(false);
    }
  }

  async function handleAtribuirMotoboy(pedidoId: number, motoboyId: string) {
    setAtribuindoId(pedidoId);

    try {
      await atribuirMotoboyPedido(pedidoId, motoboyId ? Number(motoboyId) : null);
      const atualizados = await listarPedidos();
      setPedidos(atualizados);
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Nao foi possivel atualizar a rota',
        text: error.response?.data?.erro || 'Tente novamente.',
      });
    } finally {
      setAtribuindoId(null);
    }
  }

  function renderMotoboySelect(pedido: Pedido) {
    const motoboyAtual = motoboys.find((item) => item.id === pedido.motoboy_id);

    return (
      <>
        <select
          className="lizzi-pedido-motoboy-select"
          value={pedido.motoboy_id || ''}
          onChange={(event) => void handleAtribuirMotoboy(pedido.id, event.target.value)}
          disabled={atribuindoId === pedido.id}
        >
          <option value="">Sem motoboy</option>
          {motoboysComApp.map((motoboy) => (
            <option key={motoboy.id} value={motoboy.id}>
              {motoboy.nome}
            </option>
          ))}
          {motoboyAtual && !motoboyAtual.app_motoboy_ativo ? (
            <option value={motoboyAtual.id}>{motoboyAtual.nome} (app desligado)</option>
          ) : null}
          {motoboysSemApp
            .filter((motoboy) => motoboy.id !== motoboyAtual?.id)
            .map((motoboy) => (
              <option key={motoboy.id} value={motoboy.id} disabled>
                {motoboy.nome} (app desligado)
              </option>
            ))}
        </select>
        {pedido.motoboy_id && !motoboysComApp.some((item) => item.id === pedido.motoboy_id) ? (
          <div style={{ color: '#8a5b00', fontSize: '12px', marginTop: 4 }}>Esse motoboy esta sem acesso liberado no app.</div>
        ) : null}
      </>
    );
  }

  return (
    <AppLayout>
      <div className="lizzi-cabecalho-pagina">
        <h1>
          <span className="lizzi-icone-titulo">
            <FiTruck size={18} />
          </span>
          Pedidos e entregas
        </h1>
      </div>

      <div className="lizzi-kpi-grid" style={{ marginBottom: '20px' }}>
        <Card className="lizzi-kpi-card">
          <span>Pedidos ativos</span>
          <strong>{pedidosAtivos.length}</strong>
          <p>Fila aberta para cozinha e entrega.</p>
        </Card>
        <Card className="lizzi-kpi-card">
          <span>Em entrega</span>
          <strong>{pedidosEmEntrega}</strong>
          <p>Pedidos ja roteirizados para motoboy.</p>
        </Card>
        <Card className="lizzi-kpi-card">
          <span>Clientes</span>
          <strong>{clientes.length}</strong>
          <p>Historico centralizado por nome e endereco.</p>
        </Card>
      </div>

      <div className="lizzi-grid-2">
        <Card className="lizzi-section-card">
          <div className="lizzi-section-header">
            <div>
              <h2>Novo pedido</h2>
              <p>Cadastre rapido, consulte o CEP e ja encaminhe para o motoboy certo.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="lizzi-form-grid-2">
              <Input
                label="Numero do pedido"
                value={form.numero_pedido}
                onChange={(event) => atualizarCampo('numero_pedido', event.target.value)}
                required
              />
              <Select
                label="Origem"
                value={form.origem}
                onChange={(event) => atualizarCampo('origem', event.target.value as PedidoOrigem)}
                options={ORIGENS}
              />
              <Input
                label="Nome do cliente"
                value={form.nome_cliente}
                onChange={(event) => atualizarCampo('nome_cliente', event.target.value)}
                required
              />
              <Select
                label="Motoboy"
                value={form.motoboy_id}
                onChange={(event) => atualizarCampo('motoboy_id', event.target.value)}
                options={motoboysComApp.map((motoboy) => ({ value: motoboy.id, label: motoboy.nome }))}
                placeholder="Definir depois"
              />
              <div className="lizzi-inline-field-group lizzi-auth-grid-span-2">
                <Input
                  label="CEP"
                  value={form.cep}
                  onChange={(event) => {
                    ultimoCepRef.current = '';
                    atualizarCampo('cep', formatarCep(event.target.value));
                  }}
                  maxLength={9}
                  required
                />
                <div className="lizzi-inline-field-button">
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth={false}
                    onClick={() => {
                      ultimoCepRef.current = '';
                      atualizarCampo('cep', form.cep);
                    }}
                    disabled={consultandoCep || form.cep.replace(/\D/g, '').length !== 8}
                  >
                    {consultandoCep ? 'Consultando...' : 'Buscar CEP'}
                  </Button>
                </div>
              </div>

              <Input label="Rua" value={form.logradouro} readOnly required />
              <Input
                label="Numero"
                value={form.numero_endereco}
                onChange={(event) => atualizarCampo('numero_endereco', event.target.value)}
                required
              />
              <Input label="Bairro" value={form.bairro} readOnly required />
              <Input label="Cidade" value={form.cidade} readOnly required />
              <Input label="UF" value={form.uf} readOnly required maxLength={2} />
              <Input
                label="Complemento"
                value={form.complemento}
                onChange={(event) => atualizarCampo('complemento', event.target.value)}
              />
              <Form.Group className="mb-3 lizzi-auth-grid-span-2">
                <Form.Label className="lizzi-form-label">Observacoes</Form.Label>
                <Form.Control
                  as="textarea"
                  className="lizzi-form-control lizzi-textarea"
                  value={form.observacoes}
                  onChange={(event) => atualizarCampo('observacoes', event.target.value)}
                  placeholder="Ponto de referencia, instrucoes de entrega ou observacoes internas"
                />
              </Form.Group>
            </div>

            {erroCep ? <div className="lizzi-rastreamento-erro">{erroCep}</div> : null}
            {motoboysSemApp.length > 0 ? (
              <div className="lizzi-rastreamento-erro" style={{ background: 'rgba(250, 173, 20, 0.14)', color: '#8a5b00' }}>
                {motoboysSemApp.length} motoboy(s) estao com o app desligado e ficaram fora da lista de encaminhamento.
              </div>
            ) : null}

            <div className="lizzi-equipe-actions">
              <span style={{ color: '#5c6b84', fontSize: '13px' }}>
                O cliente e o endereco entram no historico automaticamente.
              </span>
              <Button type="submit" disabled={salvando} fullWidth={false}>
                <FiSend size={15} style={{ marginRight: 6 }} />
                {salvando ? 'Encaminhando...' : 'Salvar e encaminhar'}
              </Button>
            </div>
          </form>
        </Card>

        <Card className="lizzi-section-card">
          <div className="lizzi-section-header">
            <div>
              <h2>Historico de clientes</h2>
              <p>Consulte pedidos anteriores e enderecos ja usados.</p>
            </div>
          </div>

          <div className="lizzi-public-search" style={{ marginBottom: '16px', minWidth: '100%' }}>
            <FiSearch size={16} />
            <input
              value={buscaCliente}
              onChange={(event) => setBuscaCliente(event.target.value)}
              placeholder="Buscar cliente"
            />
          </div>

          <div className="lizzi-clientes-lista">
            {clientesFiltrados.map((cliente) => (
              <div key={cliente.id} className="lizzi-cliente-card">
                <div className="lizzi-cliente-card-top">
                  <div>
                    <strong>{cliente.nome}</strong>
                    <span>
                      {cliente.total_pedidos} pedido{cliente.total_pedidos === 1 ? '' : 's'}
                    </span>
                  </div>
                  <small>{cliente.ultimo_pedido_em ? formatarData(cliente.ultimo_pedido_em) : 'Sem pedidos'}</small>
                </div>

                {cliente.enderecos.length > 0 ? (
                  <div className="lizzi-cliente-block">
                    <b>
                      <FiMapPin size={13} style={{ marginRight: 6, verticalAlign: '-2px' }} />
                      Enderecos
                    </b>
                    <ul>
                      {cliente.enderecos.slice(0, 2).map((endereco, index) => (
                        <li key={`${cliente.id}-${index}`}>
                          {endereco.logradouro}, {endereco.numero}
                          {endereco.complemento ? `, ${endereco.complemento}` : ''} | {endereco.bairro} - {endereco.cidade}/{endereco.uf}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {cliente.pedidos.length > 0 ? (
                  <div className="lizzi-cliente-block">
                    <b>Ultimos pedidos</b>
                    <ul>
                      {cliente.pedidos.slice(0, 3).map((pedido) => (
                        <li key={pedido.id}>
                          {pedido.numero_pedido ? `#${pedido.numero_pedido}` : `#${pedido.id}`} | {ORIGEM_LABEL[pedido.origem]} |{' '}
                          {STATUS_LABEL[pedido.status]}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ))}

            {clientesFiltrados.length === 0 ? (
              <div className="lizzi-empty-state">Nenhum cliente encontrado.</div>
            ) : null}
          </div>
        </Card>
      </div>

      <div style={{ marginTop: '20px' }}>
        <Card className="lizzi-section-card" padding="0">
          <div className="lizzi-section-header" style={{ padding: '24px 24px 0' }}>
            <div>
              <h2>Fila de pedidos</h2>
              <p>Reencaminhe qualquer pedido para outro motoboy sem refazer cadastro.</p>
            </div>
          </div>

          {carregando ? (
            <div style={{ padding: '0 24px 24px' }}>Carregando pedidos...</div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Origem</th>
                  <th>Status</th>
                  <th>Motoboy</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {pedidosAtivos.map((pedido) => (
                  <tr key={pedido.id}>
                    <td>
                      <strong>{pedido.numero_pedido ? `#${pedido.numero_pedido}` : `#${pedido.id}`}</strong>
                      <div style={{ color: '#5c6b84', fontSize: '12px', marginTop: 4 }}>
                        Criado em {pedido.created_at ? formatarData(pedido.created_at) : '-'}
                      </div>
                    </td>
                    <td>
                      <strong>{pedido.identificacao_cliente || pedido.cliente_nome || 'Sem nome'}</strong>
                      <div style={{ color: '#5c6b84', fontSize: '12px', marginTop: 4 }}>
                        {enderecoPedido(pedido) || 'Endereco ainda nao informado'}
                      </div>
                    </td>
                    <td>{pedido.origem ? ORIGEM_LABEL[pedido.origem] : '-'}</td>
                    <td>
                      <span className={`lizzi-status-pill ${STATUS_TONE[pedido.status]}`}>{STATUS_LABEL[pedido.status]}</span>
                    </td>
                    <td>
                      {renderMotoboySelect(pedido)}
                    </td>
                    <td>{formatarMoeda(pedido.total)}</td>
                  </tr>
                ))}

                {pedidosAtivos.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '18px', textAlign: 'center' }}>
                      Nenhum pedido ativo no momento.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
